import { selectRelativeCommentAfterDeleted } from 'src/lib/commentHelper';
import { isGeoItemMetaComment } from 'src/lib/geoItemHelper';
import type { CommentIndexParams } from 'src/models/apis/comment/commentRequest';
import type { GeoItemsMeta, GIComment } from 'src/models/geoItem';
import type { MapElemInfo } from 'src/models';
import type { IGeoItemSearchRef } from 'src/models/geoItemSearch';
import type { MapElemState } from 'src/composables/useMapSelectionCore';

interface UseMapSelectionCommentCrudDeps {
  geoItemsMeta: GeoItemsMeta;
  mapElem: MapElemState;
  setMapSelectedElemInfo: (val: MapElemInfo<GIComment> | null) => void;
  refreshCommentTab: () => void;
  getCommentSearchRef: () => IGeoItemSearchRef<CommentIndexParams> | undefined;
}

export interface MapSelectionCommentCrudContext {
  createMapSelectedElem: (obj: MapElemInfo<GIComment>) => Promise<void>;
  updateMapSelectedElem: (obj: MapElemInfo<GIComment>) => Promise<void>;
  confirmDeleteMapSelectedElem: (obj: MapElemInfo<GIComment> & { confirmMessage: string }) => Promise<void>;
  doDeleteMapSelectedElem: () => Promise<void>;
  cancelDeleteMapSelectedElem: () => Promise<void>;
}

/**
 * コメント(付箋)のCRUD。地図選択の反映は core.setMapSelectedElemInfo に一方向依存する。
 */
export function useMapSelectionCommentCrud(deps: UseMapSelectionCommentCrudDeps): MapSelectionCommentCrudContext {
  const { geoItemsMeta, mapElem, setMapSelectedElemInfo } = deps;

  const createMapSelectedElem = async(obj: MapElemInfo<GIComment>) => {
    if (!geoItemsMeta.map[obj.dataName]) {
      console.warn('createMapSelectedElem. Unknown resource type', obj.dataName);
      return;
    }
    const metaItem = geoItemsMeta.map[obj.dataName];
    try {
      const resource = await metaItem.manager.createResource(obj.data);
      resource.isSelected = true;
      metaItem.layerManager.addLayerItem(resource);
      setMapSelectedElemInfo({
        dataName: obj.dataName,
        data: resource,
      });
    } catch (e) {
      mapElem.mapSelectedElemCreateFailed++;
    }

    if (obj.dataName === 'comment') {
      deps.refreshCommentTab();
    }
  };
  const updateMapSelectedElem = async(obj: MapElemInfo<GIComment>) => {
    if (!geoItemsMeta.map[obj.dataName]) {
      console.warn('updateMapSelectedElem. Unknown resource type', obj.dataName);
      return;
    }
    const metaItem = geoItemsMeta.map[obj.dataName];
    try {
      const resource = await metaItem.manager.updateResource(obj.data);
      resource.isSelected = true;
      setMapSelectedElemInfo({
        dataName: obj.dataName,
        data: resource,
      });
      metaItem.layerManager.updateLayerItem(resource);
    } catch (e) {
      mapElem.mapSelectedElemUpdateFailed++;
    }

    if (obj.dataName === 'comment') {
      deps.refreshCommentTab();
    }
  };
  const confirmDeleteMapSelectedElem = async(obj: MapElemInfo<GIComment> & { confirmMessage: string }) => {
    mapElem.mapDeleteConfirmElem = obj;
  };
  const doDeleteMapSelectedElem = async() => {
    const obj = mapElem.mapDeleteConfirmElem;
    if (!obj) return;

    mapElem.mapDeleteConfirmElem = null;
    if (!geoItemsMeta.map[obj.dataName]) {
      console.warn('doDeleteMapSelectedElem. Unknown resource type', obj.dataName);
      return;
    }
    const metaItem = geoItemsMeta.map[obj.dataName];
    const resource = await metaItem.manager.deleteResource(obj.data);
    metaItem.layerManager.deleteLayerItem(resource);
    if (obj.dataName === 'comment' && isGeoItemMetaComment(metaItem)) {
      const relativeComments = metaItem.layerManager.getRelativeComments(obj.data);
      const selectedComment = mapElem.mapSelectedElemInfo?.data as GIComment;
      if (selectedComment && selectedComment.id !== obj.data.id) {
        relativeComments.push(selectedComment);
      }
      const giSearchComment = deps.getCommentSearchRef();
      if (!giSearchComment) return;

      const commentOrNull = await selectRelativeCommentAfterDeleted({
        deletedObj: obj,
        relativeComments,
        metaItem,
        giSearchComment,
      });
      setMapSelectedElemInfo(commentOrNull);
      deps.refreshCommentTab();
    } else {
      setMapSelectedElemInfo(null);
    }
  };
  const cancelDeleteMapSelectedElem = async() => {
    mapElem.mapDeleteConfirmElem = null;
  };

  return {
    createMapSelectedElem,
    updateMapSelectedElem,
    confirmDeleteMapSelectedElem,
    doDeleteMapSelectedElem,
    cancelDeleteMapSelectedElem,
  };
}
